import { type Highlight, type HighlightAnchor, isHighlightColor } from "../model/highlights.ts";
import { type VaultAdapter } from "./VaultAdapter.ts";

/**
 * Persistencia de los resaltados, en archivos laterales al documento.
 *
 * Viven bajo una carpeta con punto para que `walkTree` los saltee: el usuario
 * nunca los ve en el sidebar, y Obsidian tampoco. El documento resaltado queda
 * intacto.
 *
 * Un archivo por documento (y no un índice único del vault): las escrituras son
 * chicas, borrar el lateral de una nota no toca a las demás, y dos notas
 * editadas a la vez no compiten por el mismo archivo.
 */

export const HIGHLIGHTS_DIR = ".notespace/highlights";

/** `notas/idea.md` → `.notespace/highlights/notas/idea.md.json` */
export function highlightsPathFor(sourcePath: string): string {
  return `${HIGHLIGHTS_DIR}/${sourcePath}.json`;
}

interface HighlightFile {
  version: 1;
  source: string;
  highlights: Highlight[];
}

export function serializeHighlights(source: string, highlights: Highlight[]): string {
  const file: HighlightFile = { version: 1, source, highlights };
  return `${JSON.stringify(file, null, 2)}\n`;
}

/**
 * Parsea el archivo lateral, descartando cualquier entrada malformada.
 *
 * Es deliberadamente tolerante: un lateral corrupto o de una versión futura
 * hace perder resaltados, nunca impide abrir la nota. El documento es lo que
 * importa; esto es metadata.
 */
export function parseHighlights(json: string): Highlight[] {
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    return [];
  }
  const raw = (data as HighlightFile | null)?.highlights;
  if (!Array.isArray(raw)) return [];

  const out: Highlight[] = [];
  for (const h of raw) {
    if (typeof h?.id !== "string" || !isHighlightColor(h?.color)) continue;
    const anchor = parseAnchor(h.anchor);
    if (!anchor) continue;
    out.push({
      id: h.id,
      color: h.color,
      createdAt: typeof h.createdAt === "number" ? h.createdAt : Date.now(),
      anchor,
    });
  }
  return out;
}

function parseAnchor(a: unknown): HighlightAnchor | null {
  if (!a || typeof a !== "object") return null;
  const raw = a as Record<string, unknown>;

  if (raw.kind === "rect") {
    if (typeof raw.page !== "number" || !Array.isArray(raw.rects)) return null;
    const rects = raw.rects.filter(
      (r): r is [number, number, number, number] =>
        Array.isArray(r) && r.length === 4 && r.every((n) => typeof n === "number"),
    );
    if (rects.length === 0) return null;
    return {
      kind: "rect",
      page: raw.page,
      rects,
      quote: typeof raw.quote === "string" ? raw.quote : "",
      ...(raw.area === true ? { area: true } : {}),
    };
  }

  // Sin `kind` se asume texto: es el formato que escribíamos antes de que
  // existiera el PDF, y no hay motivo para tirar esos resaltados.
  if (
    typeof raw.quote !== "string" ||
    raw.quote.length === 0 ||
    typeof raw.start !== "number" ||
    typeof raw.end !== "number"
  ) {
    return null;
  }
  return {
    kind: "text",
    start: raw.start,
    end: raw.end,
    quote: raw.quote,
    prefix: typeof raw.prefix === "string" ? raw.prefix : "",
    suffix: typeof raw.suffix === "string" ? raw.suffix : "",
  };
}

/** Lee los resaltados de un documento; sin archivo lateral devuelve []. */
export async function readHighlights(
  adapter: VaultAdapter,
  sourcePath: string,
): Promise<Highlight[]> {
  try {
    return parseHighlights(await adapter.read(highlightsPathFor(sourcePath)));
  } catch {
    // Todavía no se resaltó nada en esta nota.
    return [];
  }
}

/** Escribe (o borra, si quedó vacío) el archivo lateral de un documento. */
export async function writeHighlights(
  adapter: VaultAdapter,
  sourcePath: string,
  highlights: Highlight[],
): Promise<void> {
  const path = highlightsPathFor(sourcePath);
  if (highlights.length === 0) {
    // Sin resaltados no se deja un archivo vacío dando vueltas. Va a `purge`
    // y no a la papelera: es contabilidad interna de la app, y llenar la
    // papelera de laterales vacíos taparía lo que el usuario sí borró.
    try {
      await adapter.purge(path);
    } catch {
      // No existía: nada que limpiar.
    }
    // El espejo replica la estructura de carpetas del vault, así que borrar el
    // último lateral de una carpeta dejaba el directorio vacío para siempre.
    // Nadie los juntaba nunca, y con el tiempo `.notespace/highlights` parecía
    // una copia entera del vault. Se barren acá, que es donde se ensucian.
    await adapter.pruneEmptyDirs(HIGHLIGHTS_DIR);
    return;
  }
  await adapter.write(path, serializeHighlights(sourcePath, highlights));
}

/**
 * Hace que el lateral siga al documento cuando se lo mueve o renombra.
 *
 * Está indexado por la ruta de origen, así que sin esto un PDF o una nota
 * reubicada aparece de golpe sin ningún resaltado (y queda un lateral huérfano
 * apuntando a una ruta que ya no existe). Al mover una carpeta se mueve todo su
 * subárbol de laterales de una sola vez.
 */
export async function moveHighlights(
  adapter: VaultAdapter,
  from: string,
  to: string,
  isFolder: boolean,
): Promise<void> {
  const src = isFolder ? `${HIGHLIGHTS_DIR}/${from}` : highlightsPathFor(from);
  const dest = isFolder ? `${HIGHLIGHTS_DIR}/${to}` : highlightsPathFor(to);
  try {
    await adapter.rename(src, dest);
  } catch {
    // Nunca se resaltó nada acá: no hay lateral que mover.
  }
}
